import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Gift, Info, X, Users } from "lucide-react";
import { TOS_VERSION } from "@/pages/Terms";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const initialRef = params.get("ref") || "";
    const [form, setForm] = useState({
        name: "", email: "", password: "",
        home_university: "", home_city: "",
        exchange_destination: "",
        referral_code: initialRef,
        wants_exchange_buddy: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [showBuddyInfo, setShowBuddyInfo] = useState(false);
    const [acceptedTos, setAcceptedTos] = useState(false);

    useEffect(() => {
        if (initialRef) setForm((p) => ({ ...p, referral_code: initialRef }));
    }, [initialRef]);

    const submit = async (e) => {
        e.preventDefault();
        if (!acceptedTos) {
            toast.error("Please accept the Terms of Use to create an account.");
            return;
        }
        setSubmitting(true);
        try {
            await register({ ...form, accepted_tos: true, tos_version: TOS_VERSION });
            toast.success("Account created!");
            navigate("/dashboard");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <div data-testid="register-page" className="min-h-[calc(100vh-4rem)] grid md:grid-cols-2">
            <div className="flex items-center justify-center p-6 sm:p-12 order-2 md:order-1">
                <div className="w-full max-w-md">
                    <h1 className="font-display text-3xl font-bold">Create your account</h1>
                    <p className="text-muted-foreground mt-2">Already have one?{" "}
                        <Link to="/login" className="text-primary hover:underline font-medium" data-testid="register-login-link">Log in</Link>
                    </p>

                    {initialRef && (
                        <div data-testid="register-referral-banner" className="mt-6 bg-accent/40 border border-accent rounded-2xl p-4 flex items-center gap-3">
                            <Gift className="w-5 h-5 text-primary" />
                            <div className="text-sm">Invited by a friend. Welcome!</div>
                        </div>
                    )}

                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div>
                            <Label htmlFor="name">Name</Label>
                            <Input id="name" required data-testid="register-name-input"
                                className="mt-2 rounded-xl h-11" value={form.name} onChange={f("name")} />
                        </div>
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input id="email" type="email" required data-testid="register-email-input"
                                className="mt-2 rounded-xl h-11" value={form.email} onChange={f("email")} />
                        </div>
                        <div>
                            <Label htmlFor="password">Password (min 6)</Label>
                            <Input id="password" type="password" required minLength={6} data-testid="register-password-input"
                                className="mt-2 rounded-xl h-11" value={form.password} onChange={f("password")} />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="city">Home city</Label>
                                <Input id="city" data-testid="register-home-city-input"
                                    placeholder="e.g. Amsterdam"
                                    className="mt-2 rounded-xl h-11" value={form.home_city} onChange={f("home_city")} />
                            </div>
                            <div>
                                <Label htmlFor="uni">University</Label>
                                <Input id="uni" data-testid="register-home-university-input"
                                    className="mt-2 rounded-xl h-11" value={form.home_university} onChange={f("home_university")} />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="dest">Exchange destination</Label>
                            <Input id="dest" data-testid="register-exchange-destination-input"
                                placeholder="e.g. Barcelona"
                                className="mt-2 rounded-xl h-11" value={form.exchange_destination} onChange={f("exchange_destination")} />
                        </div>
                        <p className="text-xs text-muted-foreground -mt-2" data-testid="register-english-hint">
                            Please use the <strong>English / international</strong> name for cities (e.g. <em>Rome</em> not <em>Roma</em>, <em>Munich</em> not <em>München</em>) so we can match you with buddies worldwide.
                        </p>
                        <div>
                            <Label htmlFor="ref">Referral code (optional)</Label>
                            <Input id="ref" data-testid="register-referral-input"
                                className="mt-2 rounded-xl h-11" value={form.referral_code} onChange={f("referral_code")} />
                        </div>

                        {/* Exchange Buddy opt-in */}
                        <div className="rounded-2xl border border-border/60 p-4 bg-accent/20 space-y-3">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-2 min-w-0">
                                    <Users className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                                    <div className="min-w-0">
                                        <div className="font-medium text-sm">Also match me as an Exchange Buddy</div>
                                        <div className="text-xs text-muted-foreground">
                                            Match with people going to your city (or vice versa) to swap tips even without a room.
                                        </div>
                                    </div>
                                </div>
                                <Switch
                                    checked={form.wants_exchange_buddy}
                                    onCheckedChange={(v) => setForm({ ...form, wants_exchange_buddy: v })}
                                    data-testid="register-buddy-toggle"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowBuddyInfo((s) => !s)}
                                data-testid="register-buddy-info-btn"
                                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                            >
                                {showBuddyInfo ? <X className="w-3 h-3" /> : <Info className="w-3 h-3" />}
                                {showBuddyInfo ? "Hide" : "What is an Exchange Buddy?"}
                            </button>
                            {showBuddyInfo && (
                                <div data-testid="register-buddy-info" className="text-xs text-muted-foreground bg-background rounded-xl p-3 leading-relaxed border border-border/60">
                                    An <strong>Exchange Buddy</strong> is someone going in the opposite direction of your move — you know their new city, they know yours. You skip the <strong>agency fees</strong> that landlords charge new arrivals, and help each other with tips on housing, universities, neighbourhoods, admin and social life. No room swap needed, just information.
                                </div>
                            )}
                        </div>

                        {/* Apple UGC requirement: explicit ToS acceptance before signup */}
                        <label
                            data-testid="register-tos-label"
                            className={`flex items-start gap-3 p-3 rounded-2xl border cursor-pointer transition-colors ${acceptedTos ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}
                        >
                            <input
                                type="checkbox"
                                data-testid="register-tos-checkbox"
                                checked={acceptedTos}
                                onChange={(e) => setAcceptedTos(e.target.checked)}
                                className="mt-1 w-4 h-4 accent-primary cursor-pointer"
                            />
                            <span className="text-sm leading-relaxed">
                                I agree to the{" "}
                                <Link
                                    to="/terms" target="_blank" rel="noopener noreferrer"
                                    data-testid="register-tos-link"
                                    className="text-primary hover:underline font-medium"
                                >
                                    Terms of Use
                                </Link>{" "}
                                and confirm I will not post objectionable, illegal or misleading content.
                                I understand ErasmusSwap may remove content or suspend accounts that break these rules.
                            </span>
                        </label>

                        <Button type="submit" disabled={submitting || !acceptedTos} data-testid="register-submit-btn"
                            className="w-full rounded-full h-11 bg-primary hover:bg-primary/90">
                            {submitting ? "Creating…" : "Create account"}
                        </Button>
                    </form>
                </div>
            </div>
            <div className="hidden md:block relative overflow-hidden order-1 md:order-2">
                <img
                    src="https://static.prod-images.emergentagent.com/jobs/6e4fba8f-fe4c-4e25-a28c-f4fefa54fbfa/images/8129009579c003ef5e4a280a2bfb70153af3cf6f1f474bbfa490300bee17471c.png"
                    alt=""
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-primary/10" />
                <div className="absolute bottom-10 left-10 right-10">
                    <h2 className="font-display text-3xl font-bold text-background drop-shadow">Join the swap community</h2>
                </div>
            </div>
        </div>
    );
}
